import { useConfirm } from 'material-ui-confirm';
import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

type UnsavedChangesGuardOptions = {
  title: string;
  description: string;
  confirmationText: string;
  cancellationText: string;
};

export default function useUnsavedChangesGuard(
  isDirty: boolean,
  options: UnsavedChangesGuardOptions,
) {
  const confirm = useConfirm();
  const allowNavigationRef = useRef(false);
  const confirmInFlightRef = useRef(false);

  const allowNavigation = useCallback(() => {
    allowNavigationRef.current = true;
  }, []);

  useEffect(() => {
    if (!isDirty || allowNavigationRef.current) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const shouldBlock = useCallback(
    () => isDirty && !allowNavigationRef.current,
    [isDirty],
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state !== 'blocked' || confirmInFlightRef.current) return;

    confirmInFlightRef.current = true;
    confirm({
      title: options.title,
      description: options.description,
      confirmationText: options.confirmationText,
      cancellationText: options.cancellationText,
    })
      .then(({ confirmed }) => {
        if (confirmed) {
          allowNavigationRef.current = true;
          blocker.proceed();
        } else {
          blocker.reset();
        }
      })
      .finally(() => {
        confirmInFlightRef.current = false;
      });
  }, [
    blocker,
    blocker.state,
    confirm,
    options.cancellationText,
    options.confirmationText,
    options.description,
    options.title,
  ]);

  const confirmLeave = useCallback(async (): Promise<boolean> => {
    if (!isDirty || allowNavigationRef.current) return true;

    const { confirmed } = await confirm({
      title: options.title,
      description: options.description,
      confirmationText: options.confirmationText,
      cancellationText: options.cancellationText,
    });
    if (confirmed) {
      allowNavigationRef.current = true;
      return true;
    }
    return false;
  }, [
    confirm,
    isDirty,
    options.cancellationText,
    options.confirmationText,
    options.description,
    options.title,
  ]);

  return { confirmLeave, allowNavigation };
}
